"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseBTCNetwork = exports.fetchDerivedBTCAddressAndPublicKey = exports.fetchBTCFeeProperties = exports.fetchBTCUTXOs = exports.fetchBTCFeeRate = void 0;
/* eslint-disable @typescript-eslint/prefer-ts-expect-error */
const axios_1 = __importDefault(require("axios"));
const bitcoin = __importStar(require("bitcoinjs-lib"));
// There is no types for coinselect
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const coinselect_1 = __importDefault(require("coinselect"));
const kdf_1 = require("../../kdf/kdf");
const signature_1 = require("../../signature");
const utils_1 = require("../../kdf/utils");
/**
 * Fetches the current fee rate from the Bitcoin network.
 * This method queries the RPC endpoint for fee estimates and returns the fee rate
 * expected for a transaction to be confirmed within a certain number of blocks.
 * The confirmation target is set to 6 blocks by default, which is commonly used
 * for a balance between confirmation time and cost.
 *
 * @returns {Promise<number>} A promise that resolves to the fee rate in satoshis per byte.
 * @throws {Error} Throws an error if the fee rate data for the specified confirmation target is missing.
 */
function fetchBTCFeeRate(providerUrl_1) {
    return __awaiter(this, arguments, void 0, function* (providerUrl, confirmationTarget = 6) {
        var _a;
        const response = yield axios_1.default.get(`${providerUrl}fee-estimates`);
        if ((_a = response.data) === null || _a === void 0 ? void 0 : _a[confirmationTarget]) {
            return response.data[confirmationTarget];
        }
        throw new Error(`Fee rate data for ${confirmationTarget} blocks confirmation target is missing in the response`);
    });
}
exports.fetchBTCFeeRate = fetchBTCFeeRate;
/**
 * Fetches the Unspent Transaction Outputs (UTXOs) for a given Bitcoin address.
 *
 * @param {string} address - The Bitcoin address for which to fetch the UTXOs.
 * @returns {Promise<UTXO[]>} A promise that resolves to an array of UTXOs.
 * Each UTXO is represented as an object containing the transaction ID (`txid`), the output index within that transaction (`vout`),
 * the value of the output in satoshis (`value`) and the locking script (`script`).
 */
function fetchBTCUTXOs(providerUrl, address) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield axios_1.default.get(`${providerUrl}address/${address}/utxo`);
            const utxos = response.data.map((utxo) => {
                return {
                    txid: utxo.txid,
                    vout: utxo.vout,
                    value: utxo.value,
                    script: utxo.script,
                };
            });
            return utxos;
        }
        catch (error) {
            console.error('Failed to fetch UTXOs:', error);
            return [];
        }
    });
}
exports.fetchBTCUTXOs = fetchBTCUTXOs;
/**
 * Calculates the fee properties for a Bitcoin transaction.
 * This function fetches the Unspent Transaction Outputs (UTXOs) for the given address,
 * and the fee rate for the specified confirmation target. It then uses the `coinselect` algorithm
 * to select the UTXOs to be spent and calculates the fee required for the transaction.
 *
 * @param {string} providerUrl - The Bitcoin provider url to request the fee properties from
 * @param {string} from - The Bitcoin address from which the transaction is to be sent.
 * @param {Array<{address: string, value: number}>} targets - An array of target addresses and values (in satoshis) to send.
 * @param {number} [confirmationTarget=6] - The desired number of blocks in which the transaction should be confirmed.
 * @returns {Promise<{inputs: UTXO[], outputs: {address: string, value: number}[], fee: number}>} A promise that resolves to an object containing the inputs (selected UTXOs), outputs (destination addresses and values), and the transaction fee in satoshis.
 */
function fetchBTCFeeProperties(providerUrl_1, from_1, targets_1) {
    return __awaiter(this, arguments, void 0, function* (providerUrl, from, targets, confirmationTarget = 6) {
        const utxos = yield fetchBTCUTXOs(providerUrl, from);
        const feeRate = yield fetchBTCFeeRate(providerUrl, confirmationTarget);
        // Add a small amount to the fee rate to ensure the transaction is confirmed
        const ret = (0, coinselect_1.default)(utxos, targets, Math.ceil(feeRate + 1));
        if (!ret.inputs || !ret.outputs) {
            throw new Error('Invalid transaction: coinselect failed to find a suitable set of inputs and outputs. This could be due to insufficient funds, or no inputs being available that meet the criteria.');
        }
        return ret;
    });
}
exports.fetchBTCFeeProperties = fetchBTCFeeProperties;
/**
 * Derives a Bitcoin address and its corresponding public key for a given signer ID and derivation path.
 * This method utilizes the root public key associated with the signer ID to generate a Bitcoin address
 * and public key buffer based on the specified derivation path and network.
 *
 * @param {string} signerId - The unique identifier of the signer.
 * @param {string} path - The derivation path used to generate the address.
 * @param {bitcoin.networks.Network} network - The Bitcoin network (e.g., mainnet, testnet).
 * @param {string} nearNetworkId - The network id used to interact with the NEAR blockchain.
 * @param {ChainSignatureContracts} contract - The mpc contract's accountId on the NEAR blockchain.
 * @returns {Promise<{ address: string; publicKey: Buffer }>} An object containing the derived Bitcoin address and its corresponding public key buffer.
 */
function fetchDerivedBTCAddressAndPublicKey(_a) {
    return __awaiter(this, arguments, void 0, function* ({ signerId, path, btcNetworkId, nearNetworkId, multichainContractId, }) {
        const contractRootPublicKey = yield (0, signature_1.getRootPublicKey)(multichainContractId, nearNetworkId);
        if (!contractRootPublicKey) {
            throw new Error('Failed to fetch root public key');
        }
        const derivedKey = yield (0, kdf_1.generateBTCAddress)(signerId, (0, utils_1.getCanonicalizedDerivationPath)(path), contractRootPublicKey);
        const publicKeyBuffer = Buffer.from(derivedKey, 'hex');
        const { address } = bitcoin.payments.p2pkh({
            pubkey: publicKeyBuffer,
            network: parseBTCNetwork(btcNetworkId),
        });
        if (!address) {
            throw new Error('Failed to generate Bitcoin address');
        }
        return { address, publicKey: publicKeyBuffer };
    });
}
exports.fetchDerivedBTCAddressAndPublicKey = fetchDerivedBTCAddressAndPublicKey;
function parseBTCNetwork(network) {
    switch (network.toLowerCase()) {
        case 'mainnet':
            return bitcoin.networks.bitcoin;
        case 'testnet':
            return bitcoin.networks.testnet;
        case 'regtest':
            return bitcoin.networks.regtest;
        default:
            throw new Error(`Unknown Bitcoin network: ${network}`);
    }
}
exports.parseBTCNetwork = parseBTCNetwork;
